import { supabase } from '../utils/supabaseClient';
import type { Tenant } from '@/contexts/TenantContext';

/**
 * What each Getting Started step's DATA says (true = the step's data exists).
 *
 * The step list itself — stages, labels, pitches, skip labels — lives in
 * `constants/onboardingSteps.ts`; this is only the evidence. Skips are a separate set
 * (`skippedSteps`), because "data exists" and "the owner said it doesn't apply" are different
 * facts: data wins (a skipped sales-tax step that later gets a rate reads done, not skipped).
 */
export interface OnboardingProgress {
  // Stage 1 — Cost your products
  staffProfile: boolean;     // current user's staff hourly_loaded_cost > 0 (LABOR_RATE_NOT_SET decision 6)
  suppliers: boolean;
  ingredients: boolean;
  materials: boolean;
  recipes: boolean;
  variants: boolean;
  packages: boolean;
  products: boolean;

  // Stage 2 — Get ready to sell
  customers: boolean;
  companyProfile: boolean;   // tenants.address_line_1 && city
  postOffice: boolean;       // shipping_settings.default_po_address_line_1
  taxSettings: boolean;      // tenants.default_sales_tax_rate > 0
  priceLists: boolean;
  orderOnline: boolean;      // any portal_users row — survives a revoke, unlike portal_user_customers

  // Stage 3 — Run your first orders
  purchaseOrders: boolean;
  production: boolean;       // a completed make order OR a completed weekly production log
  completedSO: boolean;      // sales_orders status='shipped'
  completedTO: boolean;      // transport_orders status='delivered'
  inviteStaff: boolean;      // 2+ joined staff or any pending invitation

  /** Step keys the owner skipped (`tenants.onboarding_skipped_steps`). */
  skippedSteps: string[];
  currentUserStaffId?: string; // for /staff/:id navigation
}

/**
 * Check if at least one row exists in the given table.
 * Uses head:true + count to avoid transferring any row data.
 */
async function hasAny(
  table: string,
  filters?: { column: string; value: string }[]
): Promise<boolean> {
  let query = supabase
    .from(table)
    .select('id', { count: 'exact', head: true });

  if (filters) {
    for (const f of filters) {
      query = query.eq(f.column, f.value);
    }
  }

  const { count, error } = await query;
  if (error) {
    console.error(`Onboarding check failed for ${table}:`, error);
    return false;
  }
  return (count ?? 0) > 0;
}

/**
 * Check if count >= minCount in a table.
 */
async function hasCount(
  table: string,
  minCount: number,
  filters?: { column: string; value: string }[],
  notNullColumns?: string[]
): Promise<boolean> {
  let query = supabase
    .from(table)
    .select('id', { count: 'exact', head: true });

  if (filters) {
    for (const f of filters) {
      query = query.eq(f.column, f.value);
    }
  }

  if (notNullColumns) {
    for (const column of notNullColumns) {
      query = query.not(column, 'is', null);
    }
  }

  const { count, error } = await query;
  if (error) {
    console.error(`Onboarding count check failed for ${table}:`, error);
    return false;
  }
  return (count ?? 0) >= minCount;
}

/**
 * Get current user's staff record: whether it has a loaded hourly rate, and its staff ID.
 */
async function getStaffForUser(
  userId: string
): Promise<{ hasWage: boolean; staffId?: string }> {
  const { data, error } = await supabase
    .from('staff_with_users')
    .select('id, hourly_loaded_cost')
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Onboarding staff profile check failed:', error);
    return { hasWage: false };
  }

  if (!data) return { hasWage: false };

  return {
    // LABOR_RATE_NOT_SET decision 6: tick on the LOADED rate — the number costs read — not base_wage. A salary with
    // 0 hours/week has a wage but no hourly rate, and "Wage added" beside "labor rate not set" would contradict itself.
    hasWage: Number(data.hourly_loaded_cost ?? 0) > 0,
    staffId: data.id,
  };
}

/**
 * Check if shipping settings have been configured.
 */
async function hasShippingConfigured(): Promise<boolean> {
  const { data, error } = await supabase
    .from('shipping_settings')
    .select('default_po_address_line_1')
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Onboarding shipping check failed:', error);
    return false;
  }

  return Boolean(data?.default_po_address_line_1);
}

/**
 * The tenant row's own evidence, read FRESH rather than from the context's cached `tenant`:
 * `refreshProgress()` runs right after a save, and the context row is a snapshot that a
 * `useCallback` may still hold from before it (session 331 review). One read covers company info,
 * sales tax and the skipped steps.
 */
async function getTenantEvidence(tenantId?: string): Promise<{
  companyProfile: boolean;
  taxSettings: boolean;
  skippedSteps: string[];
}> {
  const empty = { companyProfile: false, taxSettings: false, skippedSteps: [] };
  if (!tenantId) return empty;
  const { data, error } = await supabase
    .from('tenants')
    .select('address_line_1, city, default_sales_tax_rate, onboarding_skipped_steps')
    .eq('id', tenantId)
    .maybeSingle();

  if (error || !data) {
    if (error) console.error('Onboarding tenant check failed:', error);
    return empty;
  }

  return {
    companyProfile: Boolean(data.address_line_1 && data.city),
    taxSettings: (data.default_sales_tax_rate ?? 0) > 0,
    skippedSteps: Array.isArray(data.onboarding_skipped_steps) ? data.onboarding_skipped_steps : [],
  };
}

/**
 * Detect which onboarding steps the tenant's data has completed.
 * Everything except the tenant row uses head-only count queries (RLS scopes to tenant).
 */
export async function detectOnboardingProgress(
  tenant: Tenant | null,
  userId?: string
): Promise<OnboardingProgress> {
  const [
    tenantEvidence,
    staffResult,
    inviteStaff,
    suppliers,
    customers,
    ingredients,
    materials,
    recipes,
    variants,
    packages,
    products,
    priceLists,
    orderOnline,
    purchaseOrders,
    postOffice,
    completedMO,
    completedLog,
    completedSO,
    completedTO,
  ] = await Promise.all([
    getTenantEvidence(tenant?.id),
    userId ? getStaffForUser(userId) : Promise.resolve({ hasWage: false, staffId: undefined }),
    // inviteStaff: 2+ joined staff OR any pending invitation (no status column — row exists = pending).
    // The user_id filter is load-bearing: staff rows can now exist without an account, and adding
    // two payroll-only workers must not tick "Invite your team" for an operator who has
    // invited nobody. Only a row with a linked account counts as someone who joined.
    hasCount('staff_with_users', 2, undefined, ['user_id']).then(
      (joined) => joined || hasAny('staff_invitations')
    ),
    hasAny('suppliers'),
    hasAny('customers'),
    hasAny('resources', [{ column: 'type', value: 'ingredient' }]),
    hasAny('resources', [{ column: 'type', value: 'material' }]),
    hasAny('recipes'),
    hasAny('variants'),
    hasAny('packages'),
    hasAny('products'),
    hasAny('price_lists'),
    hasAny('portal_users'),
    hasAny('purchase_orders'),
    hasShippingConfigured(),
    hasAny('make_orders', [{ column: 'status', value: 'completed' }]),
    hasAny('weekly_labor_logs', [{ column: 'status', value: 'completed' }]),
    hasAny('sales_orders', [{ column: 'status', value: 'shipped' }]),
    hasAny('transport_orders', [{ column: 'status', value: 'delivered' }]),
  ]);

  return {
    staffProfile: staffResult.hasWage,
    suppliers,
    ingredients,
    materials,
    recipes,
    variants,
    packages,
    products,
    customers,
    companyProfile: tenantEvidence.companyProfile,
    postOffice,
    taxSettings: tenantEvidence.taxSettings,
    priceLists,
    orderOnline,
    purchaseOrders,
    production: completedMO || completedLog,
    completedSO,
    completedTO,
    inviteStaff,
    skippedSteps: tenantEvidence.skippedSteps,
    currentUserStaffId: staffResult.staffId,
  };
}

/**
 * Mark a Getting Started step as not applying (or undo that). Atomic on the server
 * (`onboarding_set_step_skipped`, SECURITY INVOKER — RLS limits it to owner/admin), so two quick
 * skips cannot erase each other. Returns the stored set. Throws on error.
 */
export async function setOnboardingStepSkipped(stepKey: string, skipped: boolean): Promise<string[]> {
  const { data, error } = await supabase.rpc('onboarding_set_step_skipped', {
    p_step: stepKey,
    p_skipped: skipped,
  });
  if (error) throw error;
  return Array.isArray(data) ? (data as string[]) : [];
}
